<template>
  <form
    class="business-register-form"
    ref="registerForm"
    @submit.prevent="validateForm"
  >
    <Title
      class="business-register-form__title"
      :header="
        `${
          invitationEmail ? `${invitationEmail}, s` : 'S'
        }ign up for a free INE account!`
      "
      :tagline="`Sign in to join ${invitationCompany}'s account.`"
    />
    <div class="business-register-form__group-wrapper">
      <div
        v-for="(field, key) in fields"
        :key="key"
        class="business-register-form__group"
        :class="[fields[key].class]"
      >
        <input
          v-if="isShown(key) && !field.hidden"
          :type="fields[key].type"
          :class="[
            'ine-input business-register-form__action',
            fieldHasError(key) && 'business-register-form__action--error',
          ]"
          :placeholder="fields[key].placeholder"
          :autocomplete="fields[key].autocomplete"
          @focus="clearError(key)"
          v-model="fields[key].value"
        />
        <span
          v-if="fieldHasError(key)"
          class="business-register-form__group--error"
          >{{ errors[key] }}</span
        >
      </div>
      <div class="business-register-form__password-wrapper">
        <p class="business-register-form__password-title">
          Password requirements
        </p>
        <PasswordVerifierWidget
          :password="fields.password.value"
          :isPasswordConfirmed="isPasswordConfirmed"
        />
      </div>
    </div>
    <div class="business-register-form__btn-wrapper">
      <p class="business-register-form__tos">
        By clicking Sign up you are explicitly agreeing to INE's
        <a
          href="//checkout.shopify.com/17217507/policies/16723909.html"
          class="business-register-form__tos--link"
          rel="noopener nofollow"
          target="_blank"
          >Terms of Service</a
        >
      </p>
      <button
        v-if="!isLoading"
        class="btn btn--primary business-register-form__btn"
        type="submit"
      >
        Sign up
      </button>
      <vue-loaders v-else name="ball-clip-rotate" color="#ef7d22"></vue-loaders>
    </div>
  </form>
</template>

<script>
import { validateForm, clearFormErrors } from '@/helpers'
import Title from '@/components/forms/elements/Title.vue'
import PasswordVerifierWidget from '@/components/forms/PasswordVerifierWidget.vue'

export default {
  name: 'RegistrationForm',
  components: {
    PasswordVerifierWidget,
    Title,
  },
  props: {
    isAuthenticated: {
      type: Boolean,
      required: true,
    },
    profile: {
      type: Object,
      required: false,
    },
    invitation: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      selectedInterest: undefined,
      errors: clearFormErrors(),
      fields: {
        email: {
          type: 'email',
          placeholder: 'Email',
          rules: [],
          value: '',
          autocomplete: 'email',
          hidden: false,
        },
        'first name': {
          type: 'text',
          placeholder: 'First Name',
          rules: ['presence'],
          value: '',
          autocomplete: 'given-name',
          class: 'business-register-form__first-name',
          hidden: false,
        },
        'last name': {
          type: 'text',
          placeholder: 'Last Name',
          rules: ['presence'],
          value: '',
          autocomplete: 'family-name',
          class: 'business-register-form__last-name',
          hidden: false,
        },
        password: {
          type: 'password',
          placeholder: 'Password',
          rules: ['presence', 'password'],
          value: '',
          autocomplete: 'new-password',
          hidden: false,
        },
        'password confirmation': {
          type: 'password',
          rules: ['presence'],
          placeholder: 'Confirm Password',
          value: '',
          autocomplete: 'new-password',
          hidden: false,
        },
      },
    }
  },
  computed: {
    isPasswordConfirmed() {
      return (
        this.fields.password.value.length > 0 &&
        this.fields.password.value ===
          this.fields['password confirmation'].value
      )
    },
    loggedInUser() {
      if (this.isAuthenticated && this.profile && this.profile) {
        const {
          profile: { email, first_name: firstName, last_name: lastName },
        } = this
        return { email, firstName, lastName }
      }
      return {
        email: '',
        firstName: '',
        lastName: '',
      }
    },
    invitationFrom() {
      return this.invitation?.invited_by?.first_name || ''
    },
    invitationCompany() {
      return this.invitation.account.name
    },
    invitationEmail() {
      return this.invitation.user_invite?.email
    },
  },
  methods: {
    isShown(key) {
      const known = ['password', 'password confirmation']
      if (this.isAuthenticated) {
        return !known.includes(key)
      }
      return key && key.length
    },
    fieldHasError(field) {
      return !!(this.errors[field] && this.errors[field].length)
    },
    validateForm() {
      const { fields } = this
      if (this.isAuthenticated) {
        delete fields.password
        delete fields['password confirmation']
      }
      const { isValid, errors } = validateForm(fields)
      this.errors = errors
      if (errors.messages.length) {
        this.$refs.registerForm.scrollIntoView()
      }
      if (isValid) {
        const {
          fields: {
            email: { value: email },
            'first name': { value: first_name },
            'last name': { value: last_name },
            password: { value: password },
          },
        } = this
        const payload = {
          email: email || this.invitationEmail,
          first_name,
          last_name,
          password,
        }
        this.$emit('submit-form', payload)
      }
    },
    updateValue(event) {
      const { value, currentValue } = event
      this[value] = currentValue
    },
    clearError(field) {
      this.errors = Object.entries(this.errors).reduce((acc, [key, val], _) => {
        if (key !== field) {
          acc[key] = val
        }
        return acc
      }, {})
    },
  },
  created() {
    const { email, firstName, lastName } = this.loggedInUser
    this.fields.email.value = email
    this.fields['first name'].value = firstName
    this.fields['last name'].value = lastName
  },
  mounted() {
    if (this.invitationEmail) {
      this.fields.email.hidden = true
    } else {
      this.fields.email.rules.push('presence')
    }
  },
}
</script>

<style lang="scss" scoped>
.business-register-form {
  width: 100%;
  font-family: var(--font-regular);
  display: flex;
  flex-direction: column;
  &__required {
    color: var(--red);
    font-family: var(--font-bold);
  }
  &__group-wrapper {
    width: 100%;
    transition: 0.5s all ease;
    display: flex;
    flex-wrap: wrap;
  }
  &__group {
    position: relative;
    margin: 0 auto 10px auto;
    width: 100%;
    &--error {
      color: var(--red);
      margin: 0 0 0 15px;
      padding: 3px 0 0 0;
      position: relative;
      max-height: 12px;
      font-size: 12px;
      display: flex;
      @include mobile {
        white-space: nowrap;
      }
      &:before {
        content: '';
        border-radius: 100%;
        background: var(--error);
        height: 8px;
        width: 8px;
        position: absolute;
        bottom: -2px;
        left: -15px;
      }
    }
  }
  &__tos {
    font-size: rem(12px);
    color: var(--white);
    margin: 0;
    &--link {
      color: var(--white);
      border-bottom: 1px solid var(--white);
    }
  }
  &__btn-wrapper {
    margin-top: 50px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__action {
    width: 100%;
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      background: var(--light-blue-gray);
    }
  }
  &__password-title {
    margin: 20px 0 0 0;
    color: var(--light-blue-gray);
  }
  @include desktop {
    &__first-name,
    &__last-name {
      width: 50%;
    }
    &__first-name {
      padding-right: 5px;
    }
    &__last-name {
      padding-left: 5px;
    }
  }
  ::v-deep .ine-form__title {
    color: var(--white);
  }
  ::v-deep .ine-form__tagline {
    color: var(--light-blue-gray);
    margin-bottom: 50px;
  }
  ::v-deep .password-verifier {
    padding: 0;
  }
}
</style>
